'use client';

import { set } from 'date-fns';
import * as React from 'react';

import { cn } from '../../utils';

type Period = 'AM' | 'PM';
type View = 'hours' | 'minutes';

type Pending = {
  /** 1–12 with a 12-hour cycle, 0–23 with a 24-hour one. */
  hour: number;
  minute: number;
  period: Period;
};

const pendingFromDate = (date: Date, hourCycle: 12 | 24, step: number): Pending => {
  const hours24 = date.getHours();
  return {
    hour: hourCycle === 12 ? hours24 % 12 || 12 : hours24,
    minute: Math.floor(date.getMinutes() / step) * step,
    period: hours24 >= 12 ? 'PM' : 'AM',
  };
};

const dateFromPending = (date: Date, pending: Pending, hourCycle: 12 | 24) => {
  const hours24 =
    hourCycle === 12 ? (pending.hour % 12) + (pending.period === 'PM' ? 12 : 0) : pending.hour;
  return set(date, { hours: hours24, minutes: pending.minute, seconds: 0, milliseconds: 0 });
};

// The dial's geometry, in SVG units: the face, the ring the numbers sit on, and (24-hour
// clocks) the inner ring for 13–23 and 00.
const SIZE = 260;
const CENTER = SIZE / 2;
const OUTER_RADIUS = 104;
const INNER_RADIUS = 66;

/** Where a value sits on the face: 0 is straight up, turning clockwise. */
const pointAt = (fraction: number, radius: number) => {
  const angle = fraction * 2 * Math.PI - Math.PI / 2;
  return { x: CENTER + radius * Math.cos(angle), y: CENTER + radius * Math.sin(angle) };
};

/** `compact` draws a smaller dial and readout, for a popover with little room around it. */
type TimeClockPanelSize = 'default' | 'compact';

type DialProps = {
  size: TimeClockPanelSize;
  view: View;
  hourCycle: 12 | 24;
  step: number;
  value: number;
  onChange: (value: number) => void;
  /** Lifting the pointer after choosing an hour moves on to the minutes. */
  onCommit: () => void;
};

/**
 * The clock face: numbers around a circle and a hand from the centre to the chosen one. Tap or
 * drag anywhere on the face to move the hand; arrow keys step it when the face has focus.
 */
const ClockDial = ({ size, view, hourCycle, step, value, onChange, onCommit }: DialProps) => {
  const svgRef = React.useRef<SVGSVGElement>(null);
  const draggingRef = React.useRef(false);

  const inner = view === 'hours' && hourCycle === 24 && (value === 0 || value > 12);
  const fraction = view === 'hours' ? (value % 12) / 12 : value / 60;
  const hand = pointAt(fraction, inner ? INNER_RADIUS : OUTER_RADIUS);

  const labels = React.useMemo(() => {
    if (view === 'hours') {
      const outer = Array.from({ length: 12 }, (_, index) => {
        const hour = index + 1;
        return {
          value: hour,
          text: String(hour),
          muted: false,
          ...pointAt(hour / 12, OUTER_RADIUS),
        };
      });
      if (hourCycle === 12) return outer;
      const innerRing = Array.from({ length: 12 }, (_, index) => {
        const hour = index === 11 ? 0 : index + 13;
        return {
          value: hour,
          text: String(hour).padStart(2, '0'),
          muted: true,
          ...pointAt((index + 1) / 12, INNER_RADIUS),
        };
      });
      return [...outer, ...innerRing];
    }
    // Minutes: a number every five, as a clock has them; the ones the step can't land on are muted.
    return Array.from({ length: 12 }, (_, index) => {
      const minute = index * 5;
      return {
        value: minute,
        text: String(minute).padStart(2, '0'),
        muted: minute % step !== 0,
        ...pointAt(minute / 60, OUTER_RADIUS),
      };
    });
  }, [view, hourCycle, step]);

  const valueAtPointer = (event: React.PointerEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * SIZE - CENTER;
    const y = ((event.clientY - rect.top) / rect.height) * SIZE - CENTER;
    const turn = (Math.atan2(y, x) + Math.PI / 2 + 2 * Math.PI) % (2 * Math.PI);
    const fractionOfTurn = turn / (2 * Math.PI);
    if (view === 'minutes') {
      return (Math.round((fractionOfTurn * 60) / step) * step) % 60;
    }
    const position = Math.round(fractionOfTurn * 12) % 12; // 0 is the top
    const onInnerRing = hourCycle === 24 && Math.hypot(x, y) < (OUTER_RADIUS + INNER_RADIUS) / 2;
    if (onInnerRing) return position === 0 ? 0 : position + 12;
    return position === 0 ? 12 : position;
  };

  const handlePointerDown = (event: React.PointerEvent<SVGSVGElement>) => {
    event.preventDefault();
    draggingRef.current = true;
    event.currentTarget.setPointerCapture(event.pointerId);
    const next = valueAtPointer(event);
    if (next !== null) onChange(next);
  };

  const handlePointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
    if (!draggingRef.current) return;
    const next = valueAtPointer(event);
    if (next !== null && next !== value) onChange(next);
  };

  const handlePointerUp = (event: React.PointerEvent<SVGSVGElement>) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    event.currentTarget.releasePointerCapture(event.pointerId);
    onCommit();
  };

  const handleKeyDown = (event: React.KeyboardEvent<SVGSVGElement>) => {
    const delta =
      event.key === 'ArrowUp' || event.key === 'ArrowRight'
        ? 1
        : event.key === 'ArrowDown' || event.key === 'ArrowLeft'
          ? -1
          : 0;
    if (delta === 0) return;
    event.preventDefault();
    if (view === 'minutes') {
      onChange((value + delta * step + 60) % 60);
      return;
    }
    if (hourCycle === 24) {
      onChange((value + delta + 24) % 24);
      return;
    }
    const next = ((value - 1 + delta + 12) % 12) + 1;
    onChange(next);
  };

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      role="slider"
      tabIndex={0}
      aria-label={view === 'hours' ? 'Hour' : 'Minute'}
      aria-valuenow={value}
      aria-valuemin={view === 'hours' ? (hourCycle === 12 ? 1 : 0) : 0}
      aria-valuemax={view === 'hours' ? (hourCycle === 12 ? 12 : 23) : 60 - step}
      className={cn(
        'focus-visible:ring-ring mx-auto touch-none select-none rounded-full focus:outline-none focus-visible:ring-2',
        size === 'compact' ? 'h-[200px] w-[200px]' : 'h-[260px] w-[260px]',
      )}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onKeyDown={handleKeyDown}
    >
      <circle cx={CENTER} cy={CENTER} r={CENTER} className="fill-muted" />
      <line
        x1={CENTER}
        y1={CENTER}
        x2={hand.x}
        y2={hand.y}
        strokeWidth={2}
        className="stroke-primary"
      />
      <circle cx={CENTER} cy={CENTER} r={4} className="fill-primary" />
      <circle cx={hand.x} cy={hand.y} r={18} className="fill-primary" />
      {labels.map((label) => {
        const selected = label.value === value;
        return (
          <text
            key={`${view}-${label.value}`}
            x={label.x}
            y={label.y}
            textAnchor="middle"
            dominantBaseline="central"
            className={cn(
              'pointer-events-none tabular-nums',
              label.muted ? 'text-sm' : 'text-base',
              selected
                ? 'fill-primary-foreground font-semibold'
                : label.muted
                  ? 'fill-muted-foreground'
                  : 'fill-foreground',
            )}
          >
            {label.text}
          </text>
        );
      })}
    </svg>
  );
};

type TimeClockPanelProps = {
  /** The time shown; only its hour and minute change, its day is kept. */
  date: Date;
  onChange: (date: Date) => void;
  hourCycle: 12 | 24;
  step: number;
  /** Lifting the pointer after choosing a minute. */
  onMinutePicked?: () => void;
  /** Default 'default'. */
  size?: TimeClockPanelSize;
};

/**
 * The readout, AM/PM and dial with no field around them: the pickers put this in a sheet or a
 * popover. Tap or drag the hour, the dial turns to the minutes, tap or drag the minute, set AM or
 * PM. The readout switches between the views.
 */
const TimeClockPanel = ({
  date,
  onChange,
  hourCycle,
  step,
  onMinutePicked,
  size = 'default',
}: TimeClockPanelProps) => {
  const [view, setView] = React.useState<View>('hours');
  const pending = pendingFromDate(date, hourCycle, step);
  const update = (next: Partial<Pending>) =>
    onChange(dateFromPending(date, { ...pending, ...next }, hourCycle));

  const compact = size === 'compact';
  const readoutText = compact ? 'text-4xl' : 'text-5xl';
  const readoutButton = cn(
    'rounded-md px-2 py-1 font-light tabular-nums transition-colors',
    readoutText,
  );

  return (
    <div className={cn('flex flex-col items-center', compact ? 'gap-3' : 'gap-4')}>
      <div className="flex items-center gap-3">
        <div className="flex items-center">
          <button
            type="button"
            aria-pressed={view === 'hours'}
            onClick={() => setView('hours')}
            className={cn(
              readoutButton,
              view === 'hours' ? 'bg-primary/10 text-primary' : 'text-muted-foreground',
            )}
          >
            {hourCycle === 12 ? pending.hour : String(pending.hour).padStart(2, '0')}
          </button>
          <span className={cn('text-muted-foreground font-light', readoutText)}>:</span>
          <button
            type="button"
            aria-pressed={view === 'minutes'}
            onClick={() => setView('minutes')}
            className={cn(
              readoutButton,
              view === 'minutes' ? 'bg-primary/10 text-primary' : 'text-muted-foreground',
            )}
          >
            {String(pending.minute).padStart(2, '0')}
          </button>
        </div>
        {hourCycle === 12 && (
          <div className="flex flex-col gap-1" role="group" aria-label="AM or PM">
            {(['AM', 'PM'] as const).map((period) => (
              <button
                key={period}
                type="button"
                aria-pressed={pending.period === period}
                onClick={() => update({ period })}
                className={cn(
                  'min-h-9 min-w-12 rounded-md border px-2 text-sm font-medium transition-colors',
                  pending.period === period
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-input text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                )}
              >
                {period}
              </button>
            ))}
          </div>
        )}
      </div>

      <ClockDial
        size={size}
        view={view}
        hourCycle={hourCycle}
        step={step}
        value={view === 'hours' ? pending.hour : pending.minute}
        onChange={(next) => update(view === 'hours' ? { hour: next } : { minute: next })}
        onCommit={() => {
          if (view === 'hours') setView('minutes');
          else onMinutePicked?.();
        }}
      />
    </div>
  );
};

export { TimeClockPanel };
export type { TimeClockPanelProps, TimeClockPanelSize };
